import { AnnotationSheet } from '../models/AnnotationSheet.js';
import { ANNOTATION_SESSION_ENDPOINT } from '../utils/constants.js';

// Import specialized services
import { ConversionService } from './ConversionService.js';
import { AnnotationBlockStore } from './AnnotationBlockStore.js';
import { MetadataService } from './MetadataService.js';
import { AnnotationManagementService } from './AnnotationManagementService.js';
import { TrackManagementService } from './TrackManagementService.js';
import { UtilityService } from './UtilityService.js';
import { authFetch } from '../utils/authFetch.js';

/**
 * Main service that coordinates annotation data operations
 * Acts as a facade to the specialized services
 */
class AnnotationDataService {
  constructor() {
    this.annotationSheet = null;
    this.listeners = [];
    this.currentStatus = "Not Started"; // Track current sheet status locally
    this.sheet_id = null;
    
    // Initialize specialized services
    this.conversionService = new ConversionService(this);
    this.annotationBlockStore = new AnnotationBlockStore({
      toUi: (domain, trackId, id) =>
        this.conversionService.domainAnnotationToUiAnnotation(domain, trackId, id),
      toDomain: local => this.conversionService.uiAnnotationToDomainAnnotation(local)
    });
    this.metadataService = new MetadataService(this);
    this.annotationManagementService = new AnnotationManagementService(this);
    this.trackManagementService = new TrackManagementService(this);
    this.utilityService = new UtilityService(this);
  }

  /**
   * Initialize the data service with data from the backend
   * In a real app, this would fetch from an API
   */
  async initialize(userId, sheetId, sessionId) {
    // In real app, fetch from backend
    // console.log(`Initializing AnnotationDataService with sheetId: ${sheetId}`);
    this.annotationSheet = await AnnotationSheet.loadSheet(userId, sheetId, sessionId);
    this.annotationBlockStore.hydrateFromSheet(this.annotationSheet);
    this.sheet_id = sheetId;
    this.session_id = sessionId;
    
    // Get current status from backend
    try {
      this.currentStatus = await this.getSheetStatus(sheetId, userId);
      // console.log(`Current sheet status: ${this.currentStatus}`);
    } catch (error) {
      console.error('Failed to get sheet status:', error);
      this.currentStatus = "Not Started"; // fallback
    }
    
    // Debug log to verify data loaded from sample.json
    // console.log('Loaded annotation sheet:', {
    //   general_info: this.annotationSheet.general_info,
    //   metadata_tracks: this.annotationSheet.metadata_tracks,
    //   instrument_tracks: this.annotationSheet.instrument_tracks
    // });
    
    // Verify that instrument tracks have annotation blocks
    if (this.annotationSheet.instrument_tracks) {
      this.annotationSheet.instrument_tracks.forEach((track, index) => {
        // console.log(`Instrument track ${index} (${track.instrument}):`, {
        //   track_id: track.track_id,
        //   blocks: track.annotation_blocks?.length || 0
        // });
      });
    }
    this.notifyListeners(true);
    return this.annotationSheet;
  }

  /**
   * Register a listener function to be called when data changes
   */
  registerListener(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of data changes
   */
  notifyListeners(init=false) {
    if (this.annotationSheet) {
      if (!init) {
        this.annotationSheet.updated = true;
        // Update status to "In Progress" when user makes any edit
        this.updateStatusToInProgress();
      }
      this.listeners.forEach(listener => listener(this.annotationSheet));
    }
  }

  /**
   * Update sheet status to "In Progress" when user makes edits
   */
  async updateStatusToInProgress() {
    if (this.currentStatus !== "In Progress" && this.sheet_id) {
      this.currentStatus = "In Progress";
    }
  }

  /**
   * Get the current annotation sheet
   */
  getAnnotationSheet() {
    if (!this.annotationSheet) {
      // console.log('AnnotationSheet was not initialized, creating a new one');
      this.annotationSheet = new AnnotationSheet('default');
    }
    return this.annotationSheet;
  }

  // Delegate to ConversionService for data conversion methods
  uiAnnotationToDomainAnnotation(uiAnnotation) {
    return this.conversionService.uiAnnotationToDomainAnnotation(uiAnnotation);
  }

  domainAnnotationToUiAnnotation(domainAnnotation, trackId, id) {
    return this.conversionService.domainAnnotationToUiAnnotation(domainAnnotation, trackId, id);
  }

  extractFormDataFromDomainAnnotation(domainAnnotation, trackId) {
    return this.conversionService.extractFormDataFromDomainAnnotation(domainAnnotation, trackId);
  }

  // Delegate to AnnotationManagementService for annotation management
  updateSongGeneralInfo(uiGeneralInfo) {
    // console.log('updateSongGenInfo Data Service (3) completed: ', uiGeneralInfo.completed);
    return this.annotationManagementService.updateSongGeneralInfo(uiGeneralInfo);
  }

  createAnnotation(uiAnnotation, options) {
    return this.annotationManagementService.createAnnotation(uiAnnotation, options);
  }

  createAnnotations(uiAnnotations, options) {
    return this.annotationManagementService.createAnnotations(uiAnnotations, options);
  }

  updateAnnotation(id, uiAnnotationOrChanges, changes, options) {
    return this.annotationManagementService.updateAnnotation(
      id,
      uiAnnotationOrChanges,
      changes,
      options
    );
  }

  moveAnnotations(changes, options) {
    return this.annotationManagementService.moveAnnotations(changes, options);
  }

  deleteAnnotation(idOrAnnotation) {
    return this.annotationManagementService.deleteAnnotation(idOrAnnotation);
  }

  deleteAnnotations(ids) {
    return this.annotationManagementService.deleteAnnotations(ids);
  }

  // Delegate to the singleton-owned TrackManagementService.
  generateNextTrackIndex(trackName) {
    return this.trackManagementService.generateNextIndex(trackName);
  }

  createTrack(track) {
    return this.trackManagementService.createTrack(track);
  }

  removeTrack(trackId) {
    return this.trackManagementService.removeTrack(trackId);
  }

  renameTrack(trackId, newTrackId, newTrackName) {
    return this.trackManagementService.renameTrack(trackId, newTrackId, newTrackName);
  }

  markTrackForDeletion(trackId, marked = true) {
    return this.trackManagementService.markTrackForDeletion(trackId, marked);
  }

  // Delegate to MetadataService for metadata operations
  getSongGeneralInfoInUiFormat() {
    return this.metadataService.getSongGeneralInfoInUiFormat();
  }

  getTempoChangesInUiFormat() {
    return this.metadataService.getTempoChangesInUiFormat();
  }

  getKeyChangesInUiFormat() {
    return this.metadataService.getKeyChangesInUiFormat();
  }

  getTimeSignaturesInUiFormat() {
    return this.metadataService.getTimeSignaturesInUiFormat();
  }

  getAllAnnotationsInUiFormat() {
    return this.metadataService.getAllAnnotationsInUiFormat();
  }

  // Tempo operations
  addTempoChange(tempoChangeData) {
    return this.metadataService.addTempoChange(tempoChangeData);
  }

  updateTempoChange(index, tempoChangeData) {
    return this.metadataService.updateTempoChange(index, tempoChangeData);
  }

  deleteTempoChange(index) {
    return this.metadataService.deleteTempoChange(index);
  }

  applyBatchTempoChanges(tempoChangeList) {
    return this.metadataService.applyBatchTempoChanges(tempoChangeList);
  }

  sortTempos() {
    return this.metadataService.sortTempos();
  }

  // Key change operations
  addKeyChange(keyChangeData) {
    return this.metadataService.addKeyChange(keyChangeData);
  }

  updateKeyChange(index, keyChangeData) {
    return this.metadataService.updateKeyChange(index, keyChangeData);
  }

  deleteKeyChange(index) {
    return this.metadataService.deleteKeyChange(index);
  }

  applyBatchKeyChanges(keyChanges) {
    return this.metadataService.applyBatchKeyChanges(keyChanges);
  }

  sortKeys() {
    return this.metadataService.sortKeys();
  }

  // Time signature operations
  addTimeSignature(timeSignatureData) {
    return this.metadataService.addTimeSignature(timeSignatureData);
  }

  updateTimeSignature(index, timeSignatureData) {
    return this.metadataService.updateTimeSignature(index, timeSignatureData);
  }

  deleteTimeSignature(index) {
    return this.metadataService.deleteTimeSignature(index);
  }

  applyBatchTimeSignatureChanges(timeSignatureRanges) {
    return this.metadataService.applyBatchTimeSignatureChanges(timeSignatureRanges);
  }

  parseTimeSignatureValue(timeSignatureStr) {
    return this.metadataService.parseTimeSignatureValue(timeSignatureStr);
  }

  sortTimeSignatures() {
    return this.metadataService.sortTimeSignatures();
  }

  // Utilities
  isValidPosition(items, position, currentIndex = -1) {
    return this.utilityService.isValidPosition(items, position, currentIndex);
  }

  getChineseNameForInstrument(instrument) {
    return this.utilityService.getChineseNameForInstrument(instrument);
  }

  /**
   * Get the current status of an annotation sheet from SheetMetaData
   */
  async getSheetStatus(sheet_id, user_id) {
    try {
      const response = await authFetch(`${ANNOTATION_SESSION_ENDPOINT}/batchGetAnnotationMetadata`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sheet_ids: [sheet_id], user_id: user_id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get sheet metadata');
      }

      const result = await response.json();
      return result[0]?.status || "Not Started";
    } catch (error) {
      console.error('Failed to get sheet status:', error);
      throw error;
    }
  }

  /**
   * Update the status of an annotation sheet
   */
  async updateSheetStatus(sheet_id, status) {
    try {
      const response = await authFetch(`${ANNOTATION_SESSION_ENDPOINT}/updateSheetStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sheet_id: sheet_id,
          session_id: this.session_id,
          status: status
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update sheet status');
      }

      const result = await response.json();
      // console.log('Sheet status updated successfully:', result);
      
      // Update local status tracking
      this.currentStatus = status;
      
      return true;
    } catch (error) {
      console.error('Failed to update sheet status:', error);
      throw error;
    }
  }

  /**
   * Save the annotation sheet to the backend
   */
  async saveAnnotationSheet(user_id, sheet_id, session_id, status) {
    // In a real app, this would send data to the backend
    // console.log('Saving annotation sheet to backend:', JSON.stringify(this.annotationSheet));

    // Call the save method on annotationSheet
    if (this.annotationSheet) {
      this.annotationBlockStore.projectOntoSheet(this.annotationSheet);
      const statusToSave = status || this.currentStatus;
      await this.annotationSheet.saveSheet(
        true,
        user_id,
        sheet_id,
        session_id,
        statusToSave
      );
      this.currentStatus = statusToSave;
    }
    
    return true;
  }
}

// Create a singleton instance
const annotationDataService = new AnnotationDataService();
export default annotationDataService;
